import { useCallback, useMemo, useRef, useState } from 'react';
import { assignAgentToDocument, assignAgentToVault, listDocuments, listVaults } from '../../../../services';
import {
  parseAgentDrivesListFromResponse,
  parseTenantDocumentListFromResponse,
} from '../../../../shared/utils/apiResponse';
import { getDocumentAssignmentExpiresAt } from '../../../../shared/utils/documentAssignmentExpiry';
import { normalizeVaultDocument } from '../../utils';
import { useAgentKnowledgeEnv } from '../../context/knowledge/AgentKnowledgeEnvContext.jsx';
import { useAgentKnowledgeDrives } from '../../context/knowledge/AgentKnowledgeDrivesContext.jsx';

const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Browse-drives drawer: list MANAGE vaults, pick docs, assign to agent (READER).
 */
export function useSlideoutAgentDrives() {
  const { agentId, userId, vaultUuid: agentVaultUuid, tenantId } = useAgentKnowledgeEnv();
  const { refetch: refreshAgentDrives } = useAgentKnowledgeDrives();

  const documentsCacheRef = useRef(new Map());
  const drivesFetchedRef = useRef(false);
  const lastFetchTimeRef = useRef(0);

  const [drives, setDrives] = useState([]);
  const [drivesLoading, setDrivesLoading] = useState(false);
  const [drivesError, setDrivesError] = useState(null);

  const [driveDocuments, setDriveDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);

  const [selectedDrive, setSelectedDrive] = useState(null);
  const [isDocumentsView, setIsDocumentsView] = useState(false);

  const [selectedDriveIds, setSelectedDriveIds] = useState(() => new Set());
  const [selectedDocumentIds, setSelectedDocumentIds] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  const resolvedTenant =
    tenantId ||
    (typeof window !== 'undefined' ? localStorage.getItem('workspace_uuid') : null) ||
    null;

  const fetchDrives = useCallback(
    async (force = false) => {
      const now = Date.now();
      const shouldFetch =
        force || !drivesFetchedRef.current || now - lastFetchTimeRef.current > CACHE_DURATION || drives.length === 0;

      if (!shouldFetch) return;

      if (!resolvedTenant) {
        setDrivesError(new Error('No workspace / tenant available'));
        return;
      }

      setDrivesLoading(true);
      setDrivesError(null);

      try {
        const response = await listVaults({
          tenant_id: resolvedTenant,
          page: 1,
          page_size: 100,
          order_by: 'created_at',
          order_direction: 'desc',
          scope: 'MANAGE',
        });
        const rawList = parseAgentDrivesListFromResponse(response);
        const safeList = Array.isArray(rawList) ? rawList : [];
        const filtered = agentVaultUuid
          ? safeList.filter((d) => (d.vaultUuid || d.uuid || d.id) !== agentVaultUuid)
          : safeList;
        setDrives(filtered);
        drivesFetchedRef.current = true;
        lastFetchTimeRef.current = now;
      } catch (error) {
        setDrivesError(error);
        setDrives([]);
      } finally {
        setDrivesLoading(false);
      }
    },
    [resolvedTenant, drives.length, agentVaultUuid],
  );

  const fetchDriveDocuments = useCallback(
    async (drive, force = false) => {
      const vaultId = drive.vaultUuid || drive.uuid || drive.id;
      if (!vaultId) return;

      const cached = documentsCacheRef.current.get(vaultId);
      if (cached && !force) {
        setSelectedDrive(drive);
        setIsDocumentsView(true);
        setDriveDocuments(cached);
        return;
      }

      setSelectedDrive(drive);
      setIsDocumentsView(true);
      setDocumentsLoading(true);
      setDocumentsError(null);

      try {
        const response = await listDocuments({
          vault_uuid: vaultId,
          page_size: 100,
        });
        const raw = parseTenantDocumentListFromResponse(response);
        const list = raw.map((d) => normalizeVaultDocument(d));
        documentsCacheRef.current.set(vaultId, list);
        setDriveDocuments(list);
      } catch (error) {
        setDocumentsError(error);
        setDriveDocuments([]);
      } finally {
        setDocumentsLoading(false);
      }
    },
    [],
  );

  const handleBackToList = useCallback(() => {
    setSelectedDrive(null);
    setIsDocumentsView(false);
    setSearchQuery('');
  }, []);

  const toggleDriveSelection = useCallback((driveId) => {
    setSelectedDriveIds((prev) => {
      const next = new Set(prev);
      if (next.has(driveId)) next.delete(driveId);
      else next.add(driveId);
      return next;
    });
  }, []);

  const toggleDocumentSelection = useCallback((documentId) => {
    setSelectedDocumentIds((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) next.delete(documentId);
      else next.add(documentId);
      return next;
    });
  }, []);

  const clearSelections = useCallback(() => {
    setSelectedDriveIds(new Set());
    setSelectedDocumentIds(new Set());
  }, []);

  const grantAccess = useCallback(async () => {
    if (!agentId) return { success: false, errors: ['No agent'] };

    setIsAssigning(true);
    const errors = [];
    const uid = userId || (typeof window !== 'undefined' ? localStorage.getItem('user_uuid') : '') || '';

    for (const driveId of selectedDriveIds) {
      try {
        await assignAgentToVault(
          driveId,
          agentId,
          'READER',
          'Agent assigned to vault via Knowledge Page',
        );
      } catch (e) {
        errors.push(`Drive: ${e?.message ?? 'failed'}`);
      }
    }

    for (const documentId of selectedDocumentIds) {
      try {
        await assignAgentToDocument({
          document_uuid: documentId,
          agent_uuid: agentId,
          expires_at: getDocumentAssignmentExpiresAt(),
          owner: uid,
          created_by: uid,
          granted_by: uid,
          privilege: 'READER',
        });
      } catch (e) {
        errors.push(`Document: ${e?.message ?? 'failed'}`);
      }
    }

    setIsAssigning(false);
    void refreshAgentDrives(true);

    return {
      success: errors.length === 0,
      errors,
    };
  }, [agentId, userId, selectedDriveIds, selectedDocumentIds, refreshAgentDrives]);

  const filteredDrives = useMemo(() => {
    if (!Array.isArray(drives)) return [];
    if (!searchQuery) return drives;
    const q = searchQuery.toLowerCase();
    return drives.filter(
      (drive) =>
        drive.name?.toLowerCase().includes(q) || drive.description?.toLowerCase().includes(q),
    );
  }, [drives, searchQuery]);

  const filteredDocuments = useMemo(() => {
    if (!Array.isArray(driveDocuments)) return [];
    if (!searchQuery) return driveDocuments;
    const q = searchQuery.toLowerCase();
    return driveDocuments.filter(
      (doc) =>
        doc.name?.toLowerCase().includes(q) || String(doc.description || '').toLowerCase().includes(q),
    );
  }, [driveDocuments, searchQuery]);

  const selectedCount = selectedDriveIds.size + selectedDocumentIds.size;

  const selectedItems = useMemo(() => {
    if (selectedDriveIds.size === 0 && selectedDocumentIds.size === 0) return [];
    const driveItems = drives
      .filter((d) => selectedDriveIds.has(d.vaultUuid || d.uuid || d.id))
      .map((d) => ({ ...d, _kind: 'drive' }));
    const docItems = driveDocuments
      .filter((d) => selectedDocumentIds.has(d.uuid || d.id))
      .map((d) => ({ ...d, _kind: 'document' }));
    return [...driveItems, ...docItems];
  }, [drives, driveDocuments, selectedDriveIds, selectedDocumentIds]);

  const resetState = useCallback(() => {
    setSelectedDrive(null);
    setIsDocumentsView(false);
    setSearchQuery('');
    clearSelections();
  }, [clearSelections]);

  return {
    drives: filteredDrives,
    drivesLoading,
    drivesError,
    fetchDrives,
    driveDocuments: filteredDocuments,
    documentsLoading,
    documentsError,
    fetchDriveDocuments,
    selectedDrive,
    isDocumentsView,
    handleBackToList,
    selectedDriveTitle: selectedDrive?.name || '',
    selectedDriveIds,
    selectedDocumentIds,
    toggleDriveSelection,
    toggleDocumentSelection,
    clearSelections,
    selectedCount,
    selectedItems,
    searchQuery,
    setSearchQuery,
    grantAccess,
    isAssigning,
    resetState,
  };
}
